export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
export type Role = 'PUBLIC' | 'ROLE_USER' | 'ROLE_SUPPORT' | 'ROLE_ADMIN'

export interface QueryParam {
  name: string
  type: string
  required?: boolean
  description: string
}

export interface Endpoint {
  method: Method
  path: string
  description: string
  roles: Role[]
  queryParams?: QueryParam[]
  requestBody?: object
  responseBody?: object | null
}

export interface Group {
  name: string
  description: string
  endpoints: Endpoint[]
}

export const API_GROUPS: Group[] = [
  // ─── Auth ────────────────────────────────────────────
  {
    name: 'Auth',
    description: 'User authentication and registration.',
    endpoints: [
      {
        method: 'POST',
        path: '/api/login',
        description: 'Authenticate with email and password. Returns a JWT token and user object.',
        roles: ['PUBLIC'],
        requestBody: { email: 'user@example.com', password: 'Password1!' },
        responseBody: {
          token: 'eyJhbGciOiJSUzI1NiJ9...',
          user: { id: 'uuid', email: 'user@example.com', name: 'Jane Smith', locale: 'en' },
        },
      },
      {
        method: 'POST',
        path: '/api/register',
        description:
          'Create a new account. Returns a JWT token immediately — no separate login needed.',
        roles: ['PUBLIC'],
        requestBody: {
          email: 'jane@example.com',
          password: 'Password1!',
          name: 'Jane Smith',
          country: 'MX (optional, ISO 3166-1 alpha-2 — falls back to GeoIp if absent)',
        },
        responseBody: {
          token: 'eyJhbGciOiJSUzI1NiJ9...',
          user: { id: 'uuid', email: 'jane@example.com', name: 'Jane Smith' },
        },
      },
      {
        method: 'POST',
        path: '/api/auth/google',
        description:
          'Sign in or create an account using a Google OAuth access token obtained client-side.',
        roles: ['PUBLIC'],
        requestBody: { accessToken: 'ya29.a0AfH6SMB...' },
        responseBody: { token: 'eyJhbGciOiJSUzI1NiJ9...' },
      },
    ],
  },

  // ─── Profile ─────────────────────────────────────────
  {
    name: 'Profile',
    description: "Manage the authenticated user's own profile.",
    endpoints: [
      {
        method: 'GET',
        path: '/api/profile',
        description: "Get the current user's profile.",
        roles: ['ROLE_USER'],
        responseBody: {
          id: 'uuid',
          email: 'jane@example.com',
          name: 'Jane Smith',
          displayName: null,
          locale: 'en',
          country: 'MX',
          isPrivate: false,
          avatarUrl: 'https://imagedelivery.net/.../public',
          followersCount: 12,
          followingCount: 5,
          roles: ['ROLE_USER'],
          createdAt: '2024-01-01T00:00:00Z',
        },
      },
      {
        method: 'PUT',
        path: '/api/profile',
        description:
          "Update the current user's name, email, locale, country, privacy setting, or avatar. Pass avatarId + avatarUrl after uploading via POST /api/images/upload.",
        roles: ['ROLE_USER'],
        requestBody: {
          name: 'Jane Doe',
          email: 'jane@example.com',
          locale: 'es',
          country: 'MX',
          isPrivate: false,
          displayName: 'Jay',
          avatarId: 'cf-image-id',
          avatarUrl: 'https://imagedelivery.net/.../public',
        },
        responseBody: {
          id: 'uuid',
          name: 'Jane Doe',
          locale: 'es',
          country: 'MX',
          avatarUrl: 'https://imagedelivery.net/.../public',
        },
      },
      {
        method: 'PUT',
        path: '/api/profile/password',
        description: "Change the current user's password.",
        roles: ['ROLE_USER'],
        requestBody: { currentPassword: 'OldPassword1!', newPassword: 'NewPassword1!' },
        responseBody: { message: 'Password changed successfully' },
      },
    ],
  },

  // ─── Images ──────────────────────────────────────────
  {
    name: 'Images',
    description:
      'Upload images to Cloudflare Images. Returns variant URLs for use in recipe or profile fields.',
    endpoints: [
      {
        method: 'POST',
        path: '/api/images/upload',
        description:
          'Upload an image file (multipart/form-data, field: "file"). Max 10 MB. Accepted types: JPEG, PNG, WebP. Returns Cloudflare image IDs and variant URLs.',
        roles: ['ROLE_USER'],
        requestBody: { file: '(binary)' },
        responseBody: {
          imageId: 'cf-image-uuid',
          thumbnailUrl: 'https://imagedelivery.net/{hash}/cf-image-uuid/thumbnail',
          cardUrl: 'https://imagedelivery.net/{hash}/cf-image-uuid/card',
          publicUrl: 'https://imagedelivery.net/{hash}/cf-image-uuid/public',
        },
      },
    ],
  },

  // ─── Catalog ─────────────────────────────────────────
  {
    name: 'Catalog',
    description:
      'Manage catalog entities: allergens, food groups, seasons, units and ingredients. All write operations require ROLE_ADMIN.',
    endpoints: [
      {
        method: 'GET',
        path: '/api/allergens',
        description: 'List all allergens with translations.',
        roles: ['ROLE_USER'],
        queryParams: [
          { name: 'page', type: 'integer', description: 'Page number (default 1)' },
          { name: 'perPage', type: 'integer', description: 'Items per page (default 20)' },
          { name: 'locale', type: 'string', description: 'Translation locale (en, es)' },
        ],
        responseBody: { items: [{ id: 'uuid', name: 'Gluten', translations: {} }], total: 12 },
      },
      {
        method: 'POST',
        path: '/api/allergens',
        description: 'Create a new allergen with translations.',
        roles: ['ROLE_ADMIN'],
        requestBody: { translations: { en: { name: 'Gluten' }, es: { name: 'Gluten' } } },
        responseBody: { id: 'uuid', name: 'Gluten' },
      },
      {
        method: 'PUT',
        path: '/api/allergens/{id}',
        description: 'Update an allergen.',
        roles: ['ROLE_ADMIN'],
        requestBody: { translations: { en: { name: 'Gluten' }, es: { name: 'Gluten' } } },
        responseBody: { id: 'uuid', name: 'Gluten' },
      },
      {
        method: 'DELETE',
        path: '/api/allergens/{id}',
        description: 'Delete an allergen.',
        roles: ['ROLE_ADMIN'],
        responseBody: null,
      },
      {
        method: 'GET',
        path: '/api/food-groups',
        description: 'List all food groups.',
        roles: ['ROLE_USER'],
        responseBody: { items: [{ id: 'uuid', name: 'Dairy' }], total: 8 },
      },
      {
        method: 'POST',
        path: '/api/food-groups',
        description: 'Create a food group.',
        roles: ['ROLE_ADMIN'],
        requestBody: { translations: { en: { name: 'Dairy' }, es: { name: 'Lácteos' } } },
        responseBody: { id: 'uuid', name: 'Dairy' },
      },
      {
        method: 'PUT',
        path: '/api/food-groups/{id}',
        description: 'Update a food group.',
        roles: ['ROLE_ADMIN'],
        requestBody: { translations: { en: { name: 'Dairy' }, es: { name: 'Lácteos' } } },
        responseBody: { id: 'uuid', name: 'Dairy' },
      },
      {
        method: 'DELETE',
        path: '/api/food-groups/{id}',
        description: 'Delete a food group.',
        roles: ['ROLE_ADMIN'],
        responseBody: null,
      },
      {
        method: 'GET',
        path: '/api/seasons',
        description: 'List all seasons.',
        roles: ['ROLE_USER'],
        responseBody: { items: [{ id: 'uuid', name: 'Summer' }], total: 4 },
      },
      {
        method: 'POST',
        path: '/api/seasons',
        description: 'Create a season.',
        roles: ['ROLE_ADMIN'],
        requestBody: { translations: { en: { name: 'Summer' }, es: { name: 'Verano' } } },
        responseBody: { id: 'uuid', name: 'Summer' },
      },
      {
        method: 'PUT',
        path: '/api/seasons/{id}',
        description: 'Update a season.',
        roles: ['ROLE_ADMIN'],
        requestBody: { translations: { en: { name: 'Summer' }, es: { name: 'Verano' } } },
        responseBody: { id: 'uuid', name: 'Summer' },
      },
      {
        method: 'DELETE',
        path: '/api/seasons/{id}',
        description: 'Delete a season.',
        roles: ['ROLE_ADMIN'],
        responseBody: null,
      },
      {
        method: 'GET',
        path: '/api/units',
        description: 'List all measurement units.',
        roles: ['ROLE_USER'],
        responseBody: { items: [{ id: 'uuid', name: 'gram', abbreviation: 'g' }], total: 15 },
      },
      {
        method: 'POST',
        path: '/api/units',
        description: 'Create a unit.',
        roles: ['ROLE_ADMIN'],
        requestBody: {
          translations: {
            en: { name: 'gram', abbreviation: 'g' },
            es: { name: 'gramo', abbreviation: 'g' },
          },
        },
        responseBody: { id: 'uuid', name: 'gram', abbreviation: 'g' },
      },
      {
        method: 'PUT',
        path: '/api/units/{id}',
        description: 'Update a unit.',
        roles: ['ROLE_ADMIN'],
        requestBody: { translations: { en: { name: 'gram', abbreviation: 'g' } } },
        responseBody: { id: 'uuid', name: 'gram' },
      },
      {
        method: 'DELETE',
        path: '/api/units/{id}',
        description: 'Delete a unit.',
        roles: ['ROLE_ADMIN'],
        responseBody: null,
      },
      {
        method: 'GET',
        path: '/api/ingredients',
        description: 'List all ingredients with pagination.',
        roles: ['ROLE_USER'],
        queryParams: [
          { name: 'page', type: 'integer', description: 'Page number' },
          { name: 'perPage', type: 'integer', description: 'Items per page' },
          { name: 'locale', type: 'string', description: 'Locale for translations' },
        ],
        responseBody: {
          items: [{ id: 'uuid', name: 'Tomato', foodGroup: { id: 'uuid', name: 'Vegetable' } }],
          total: 216,
        },
      },
      {
        method: 'GET',
        path: '/api/ingredients/search',
        description: 'Autocomplete search for ingredients by name.',
        roles: ['ROLE_USER'],
        queryParams: [
          { name: 'q', type: 'string', required: true, description: 'Search query' },
          { name: 'locale', type: 'string', description: 'Locale (en, es)' },
          { name: 'limit', type: 'integer', description: 'Max results (default 10)' },
        ],
        responseBody: [
          { id: 'uuid', name: 'Tomato' },
          { id: 'uuid', name: 'Cherry Tomato' },
        ],
      },
      {
        method: 'POST',
        path: '/api/ingredients',
        description: 'Create an ingredient.',
        roles: ['ROLE_ADMIN'],
        requestBody: {
          foodGroupId: 'uuid',
          allergenIds: ['uuid'],
          translations: { en: { name: 'Tomato' }, es: { name: 'Tomate' } },
        },
        responseBody: { id: 'uuid', name: 'Tomato' },
      },
      {
        method: 'PUT',
        path: '/api/ingredients/{id}',
        description: 'Update an ingredient.',
        roles: ['ROLE_ADMIN'],
        requestBody: { translations: { en: { name: 'Tomato' } } },
        responseBody: { id: 'uuid', name: 'Tomato' },
      },
      {
        method: 'DELETE',
        path: '/api/ingredients/{id}',
        description: 'Delete an ingredient.',
        roles: ['ROLE_ADMIN'],
        responseBody: null,
      },
    ],
  },

  // ─── Catalog review ──────────────────────────────────
  {
    name: 'Catalog review',
    description:
      'Free text users typed instead of picking a catalog ingredient/unit. {kind} = ingredient | unit. Each decision is stored as an alias that also auto-links future recipes; authors get an accept/keep suggestion (see POST /api/recipes/{id}/ingredient-suggestions/{suggestionId}).',
    endpoints: [
      {
        method: 'GET',
        path: '/api/admin/catalog-review/{kind}',
        description:
          'Unmatched texts without a decision, grouped by normalized form (case/accents/spaces), most-used first.',
        roles: ['ROLE_ADMIN'],
        queryParams: [
          { name: 'page', type: 'integer', description: 'Page (default 1)' },
          { name: 'perPage', type: 'integer', description: 'Items per page (default 15)' },
        ],
        responseBody: {
          items: [
            {
              text: 'Mayonese',
              normalizedText: 'mayonese',
              variants: ['Mayonese', 'mayonése'],
              uses: 3,
              recipeCount: 2,
              firstSeen: '2026-10-09T15:00:00+00:00',
              recipes: [{ id: 'uuid', name: 'Club sandwich' }],
            },
          ],
          total: 1,
          page: 1,
          perPage: 15,
        },
      },
      {
        method: 'GET',
        path: '/api/admin/catalog-review/{kind}/suggestions',
        description: 'Closest catalog items by trigram similarity across every locale.',
        roles: ['ROLE_ADMIN'],
        queryParams: [
          { name: 'text', type: 'string', required: true, description: 'Text to match' },
        ],
        responseBody: {
          items: [{ id: 'uuid', names: { en: 'Mayonnaise', es: 'Mayonesa' }, score: 0.778 }],
        },
      },
      {
        method: 'POST',
        path: '/api/admin/catalog-review/{kind}/map',
        description:
          'Map the text to an existing catalog item and link every recipe using it (re-maps an existing alias).',
        roles: ['ROLE_ADMIN'],
        requestBody: { text: 'Mayonese', targetId: 'uuid' },
        responseBody: { alias: { id: 'uuid', status: 'mapped' }, linked: { rows: 3, recipes: 2 } },
      },
      {
        method: 'POST',
        path: '/api/admin/catalog-review/ingredient/create',
        description: 'Create a catalog ingredient for the text and link every recipe using it.',
        roles: ['ROLE_ADMIN'],
        requestBody: {
          text: 'chiltepin',
          foodGroupId: 'uuid',
          translations: [
            { locale: 'en', name: 'Chiltepin pepper' },
            { locale: 'es', name: 'Chile chiltepín' },
          ],
        },
        responseBody: { alias: {}, ingredientId: 'uuid', linked: { rows: 1, recipes: 1 } },
      },
      {
        method: 'POST',
        path: '/api/admin/catalog-review/{kind}/dismiss',
        description: 'Leave the text as free text and remove it from the queue.',
        roles: ['ROLE_ADMIN'],
        requestBody: { text: 'fghfghf' },
        responseBody: { alias: { id: 'uuid', status: 'dismissed' } },
      },
      {
        method: 'GET',
        path: '/api/admin/catalog-review/{kind}/aliases',
        description: 'Decisions made so far, newest first.',
        roles: ['ROLE_ADMIN'],
        responseBody: {
          items: [
            { id: 'uuid', text: 'Mayonese', status: 'mapped', target: { name: 'Mayonnaise' } },
          ],
          total: 1,
        },
      },
      {
        method: 'DELETE',
        path: '/api/admin/catalog-review/{kind}/aliases/{id}',
        description:
          'Forget a decision: stops auto-linking; recipes already linked keep their link.',
        roles: ['ROLE_ADMIN'],
        responseBody: null,
      },
    ],
  },

  // ─── Tags ────────────────────────────────────────────
  {
    name: 'Tags',
    description: 'Popular recipe tags for browse filtering.',
    endpoints: [
      {
        method: 'GET',
        path: '/api/tags/popular',
        description:
          'Returns the most-used recipe tags ordered by recipe count descending. Used to populate the tag chip strip on browse-recipes.',
        roles: ['PUBLIC'],
        queryParams: [
          {
            name: 'limit',
            type: 'integer',
            description: 'Max number of tags to return (default 12)',
          },
        ],
        responseBody: [
          { name: 'italian', count: 42 },
          { name: 'quick', count: 35 },
        ],
      },
    ],
  },

  // ─── Recipes ─────────────────────────────────────────
  {
    name: 'Recipes',
    description: 'Create and manage recipes, ingredients, steps, ratings, comments and co-authors.',
    endpoints: [
      {
        method: 'GET',
        path: '/api/recipes',
        description:
          'Browse published recipes. Supports filtering and pagination. No auth required. Private-user recipes show anonymous author. tags[] is an OR filter.',
        roles: ['PUBLIC'],
        queryParams: [
          { name: 'page', type: 'integer', description: 'Page number' },
          { name: 'perPage', type: 'integer', description: 'Items per page' },
          { name: 'search', type: 'string', description: 'Full-text search query' },
          { name: 'difficulty', type: 'string', description: 'easy | medium | hard' },
          { name: 'maxTime', type: 'integer', description: 'Max total time in minutes' },
          {
            name: 'tags[]',
            type: 'string',
            description: 'Filter by tag name (repeatable, OR logic)',
          },
        ],
        responseBody: {
          data: [
            {
              id: 'uuid',
              title: 'Pasta Bolognese',
              difficulty: 'medium',
              totalTime: 45,
              author: { id: 'uuid', name: 'Jane Smith' },
            },
          ],
          total: 200,
          page: 1,
          limit: 20,
        },
      },
      {
        method: 'POST',
        path: '/api/recipes',
        description:
          'Create a new recipe. Upload the image first via POST /api/images/upload, then pass imageId + imageUrl here.',
        roles: ['ROLE_USER'],
        requestBody: {
          name: 'Pasta Bolognese',
          description: 'Classic Italian meat sauce pasta.',
          difficulty: 'medium',
          prepTime: 15,
          cookTime: 30,
          servings: 4,
          imageId: 'cf-image-uuid',
          imageUrl: 'https://imagedelivery.net/{hash}/cf-image-uuid/card',
          sections: [{ key: 'client-key-1', title: 'Sauce' }],
          ingredients: [
            { ingredientId: 'uuid', amount: '200', unit: 'g', name: 'Spaghetti', optional: false },
            { name: 'Tomatoes', amount: 3, sectionKey: 'client-key-1' },
          ],
          steps: [
            { instruction: 'Boil water.' },
            { instruction: 'Simmer.', sectionKey: 'client-key-1' },
          ],
          tags: ['italian', 'pasta'],
        },
        responseBody: {
          id: 'uuid',
          name: 'Pasta Bolognese',
          imageUrl: 'https://imagedelivery.net/...',
        },
      },
      {
        method: 'PUT',
        path: '/api/recipes/{id}',
        description:
          'Update a recipe. Only the author or a co-author can edit. Pass a new imageId to replace the cover image — the old Cloudflare image is deleted automatically.',
        roles: ['ROLE_USER'],
        requestBody: {
          name: 'Pasta Bolognese',
          difficulty: 'easy',
          imageId: 'new-cf-image-uuid',
          imageUrl: 'https://imagedelivery.net/...',
        },
        responseBody: {
          id: 'uuid',
          name: 'Pasta Bolognese',
          imageUrl: 'https://imagedelivery.net/...',
        },
      },
      {
        method: 'DELETE',
        path: '/api/recipes/{id}',
        description: 'Delete a recipe. Returns 204 No Content.',
        roles: ['ROLE_USER'],
        responseBody: null,
      },
      {
        method: 'GET',
        path: '/api/recipes/my/created',
        description: 'List recipes created by or co-authored by the current user.',
        roles: ['ROLE_USER'],
        responseBody: { items: [{ id: 'uuid', title: 'Pasta Bolognese' }], total: 5 },
      },
      {
        method: 'GET',
        path: '/api/recipes/my/saved',
        description: 'List recipes saved/bookmarked by the current user.',
        roles: ['ROLE_USER'],
        responseBody: { items: [{ id: 'uuid', title: 'Tiramisu' }], total: 3 },
      },
      {
        method: 'POST',
        path: '/api/recipes/{id}/save',
        description: 'Toggle save/unsave a recipe. Returns the new saved state.',
        roles: ['ROLE_USER'],
        responseBody: { saved: true },
      },
      {
        method: 'POST',
        path: '/api/recipes/{id}/rate',
        description: 'Rate a recipe from 1 to 5 stars.',
        roles: ['ROLE_USER'],
        requestBody: { rating: 5 },
        responseBody: { averageRating: 4.7, ratingCount: 23 },
      },
      {
        method: 'GET',
        path: '/api/recipes/{id}',
        description:
          'Get full recipe detail. No auth required. Private-user recipes show author as Anonymous.',
        roles: ['PUBLIC'],
        responseBody: {
          id: 'uuid',
          title: 'Pasta Bolognese',
          description: 'Classic Italian meat sauce pasta.',
          difficulty: 'medium',
          prepTime: 15,
          cookTime: 30,
          servings: 4,
          imageId: 'cf-image-uuid',
          imageUrl: 'https://imagedelivery.net/...',
          videoUrl: null,
          tags: [{ name: 'italian' }],
          images: [
            { imageId: 'cf-image-uuid', imageUrl: 'https://imagedelivery.net/...', position: 0 },
          ],
          sections: [{ id: 'uuid', title: 'Sauce', position: 0 }],
          ingredients: [],
          steps: [],
          nutrition: null,
          author: { id: 'uuid', name: 'Jane Smith' },
          averageRating: 4.2,
          ratingsCount: 12,
          savedCount: 5,
          isSaved: false,
          myRating: null,
        },
      },
      {
        method: 'GET',
        path: '/api/recipes/{id}/comments',
        description: 'List comments for a recipe. No auth required.',
        roles: ['PUBLIC'],
        responseBody: {
          items: [
            {
              id: 'uuid',
              parentId: null,
              content: 'Delicious!',
              imageId: null,
              imageUrl: null,
              author: { id: 'uuid', name: 'Jane', email: 'jane@example.com' },
              createdAt: '2024-01-01T00:00:00Z',
            },
          ],
          total: 8,
        },
      },
      {
        method: 'POST',
        path: '/api/recipes/{id}/comments',
        description:
          'Post a comment. Optionally include parentId for a reply and up to 3 images. Send X-Idempotency-Key header to safely retry without creating duplicates.',
        roles: ['ROLE_USER'],
        requestBody: {
          content: 'This is amazing!',
          parentId: 'uuid (optional)',
          images: [{ imageId: 'cf-image-uuid', imageUrl: 'https://imagedelivery.net/...' }],
        },
        responseBody: {
          id: 'uuid',
          parentId: null,
          content: 'This is amazing!',
          images: [
            { imageId: 'cf-image-uuid', imageUrl: 'https://imagedelivery.net/...', position: 0 },
          ],
          author: { id: 'uuid', name: 'Jane', email: 'jane@example.com' },
          createdAt: '2024-01-01T00:00:00Z',
        },
      },
      {
        method: 'DELETE',
        path: '/api/recipes/{id}/comments/{commentId}',
        description: 'Delete a comment. Authors can delete their own; admins can delete any.',
        roles: ['ROLE_USER'],
        responseBody: null,
      },
      {
        method: 'GET',
        path: '/api/recipes/{id}/co-authors',
        description: 'List co-authors of a recipe.',
        roles: ['ROLE_USER'],
        responseBody: [{ id: 'uuid', name: 'John Doe', email: 'john@example.com' }],
      },
      {
        method: 'POST',
        path: '/api/recipes/{id}/co-authors/{userId}',
        description: 'Add a co-author to a recipe. Only the recipe author can do this.',
        roles: ['ROLE_USER'],
        responseBody: { message: 'Co-author added' },
      },
      {
        method: 'DELETE',
        path: '/api/recipes/{id}/co-authors/{userId}',
        description: 'Remove a co-author from a recipe.',
        roles: ['ROLE_USER'],
        responseBody: null,
      },
    ],
  },

  // ─── Social ──────────────────────────────────────────
  {
    name: 'Social',
    description: 'Follow users, browse profiles, and get a personalised feed.',
    endpoints: [
      {
        method: 'GET',
        path: '/api/social/users',
        description: 'Search users by name or email.',
        roles: ['ROLE_USER'],
        queryParams: [{ name: 'q', type: 'string', required: true, description: 'Search query' }],
        responseBody: [{ id: 'uuid', name: 'Jane Smith', avatarUrl: null, isPrivate: false }],
      },
      {
        method: 'GET',
        path: '/api/social/users/{id}',
        description:
          "Get a user's public profile. Returns 403 if the user is private and not followed.",
        roles: ['ROLE_USER'],
        responseBody: {
          id: 'uuid',
          name: 'Jane Smith',
          avatarUrl: 'https://imagedelivery.net/...',
          followerCount: 120,
          followingCount: 45,
          isPrivate: false,
        },
      },
      {
        method: 'POST',
        path: '/api/social/users/{id}/follow',
        description: 'Toggle follow/unfollow a user.',
        roles: ['ROLE_USER'],
        responseBody: { following: true },
      },
      {
        method: 'GET',
        path: '/api/social/users/{id}/followers',
        description: 'List followers of a user.',
        roles: ['ROLE_USER'],
        responseBody: [{ id: 'uuid', name: 'John Doe' }],
      },
      {
        method: 'GET',
        path: '/api/social/users/{id}/following',
        description: 'List users that this user follows.',
        roles: ['ROLE_USER'],
        responseBody: [{ id: 'uuid', name: 'Jane Smith' }],
      },
      {
        method: 'GET',
        path: '/api/social/feed',
        description: 'Get the recipe feed from followed users.',
        roles: ['ROLE_USER'],
        responseBody: {
          items: [{ id: 'uuid', title: 'Greek Salad', author: { name: 'Jane Smith' } }],
          total: 40,
        },
      },
    ],
  },

  // ─── Users (admin) ───────────────────────────────────
  {
    name: 'Users (Admin)',
    description:
      'Admin-only user management: create, update, reset passwords, clear social integrations, and deactivate accounts.',
    endpoints: [
      {
        method: 'GET',
        path: '/api/users',
        description: 'Paginated list of all users.',
        roles: ['ROLE_ADMIN'],
        queryParams: [
          { name: 'page', type: 'integer', description: 'Page number' },
          { name: 'perPage', type: 'integer', description: 'Items per page' },
        ],
        responseBody: {
          items: [
            {
              id: 'uuid',
              email: 'jane@example.com',
              name: 'Jane',
              isAdmin: false,
              active: true,
              integrations: { google: true, apple: false },
            },
          ],
          total: 150,
        },
      },
      {
        method: 'POST',
        path: '/api/users',
        description: 'Create a new user account.',
        roles: ['ROLE_ADMIN'],
        requestBody: {
          email: 'jane@example.com',
          name: 'Jane Smith',
          password: 'Password1!',
          isAdmin: false,
          locale: 'en',
        },
        responseBody: { id: 'uuid', email: 'jane@example.com', name: 'Jane Smith' },
      },
      {
        method: 'PUT',
        path: '/api/users/{id}',
        description:
          "Update a user's name, email, role, locale, active status, or support access. Set supportRestricted: true to block the user from submitting or viewing support tickets.",
        roles: ['ROLE_ADMIN'],
        requestBody: {
          name: 'Jane Doe',
          isAdmin: true,
          active: true,
          locale: 'es',
          supportRestricted: false,
        },
        responseBody: {
          id: 'uuid',
          name: 'Jane Doe',
          isAdmin: true,
          supportRestricted: false,
        },
      },
      {
        method: 'GET',
        path: '/api/users/{id}',
        description:
          "User detail for the admin user screen: account, subscription, profile, stats and this month's AI caps.",
        roles: ['ROLE_ADMIN'],
        responseBody: {
          id: 'uuid',
          email: 'jane@example.com',
          subscription: {
            tier: 'pro',
            billingPeriod: 'monthly',
            periodEnd: '2026-11-01T00:00:00+00:00',
            stripeCustomer: 'cus_123',
          },
          stats: { recipes: 12, followers: 40, following: 8 },
          ai: { dailyLimit: 20, quotas: [{ feature: 'recipe_translation', used: 3, limit: 30 }] },
        },
      },
      {
        method: 'GET',
        path: '/api/users/{id}/ai-usage?from=YYYY-MM-DD&to=YYYY-MM-DD',
        description:
          'AI usage audit for one user (UTC, `to` inclusive, max one year; defaults to the current month): totals, per-feature breakdown, daily series and the latest 200 calls with token counts and estimated cost.',
        roles: ['ROLE_ADMIN'],
        responseBody: {
          totals: { calls: 42, inputTokens: 120000, outputTokens: 30000, cost: 0.31 },
          features: [
            {
              feature: 'meal_plan',
              calls: 4,
              inputTokens: 18000,
              outputTokens: 4000,
              cost: 0.038,
              monthlyLimit: null,
            },
          ],
          daily: [{ date: '2026-10-07', calls: 6, cost: 0.04 }],
          calls: [
            {
              id: 'uuid',
              feature: 'meal_plan',
              model: 'claude-haiku-4-5-20251001',
              inputTokens: 4500,
              outputTokens: 1000,
              cost: 0.0095,
              createdAt: '2026-10-07T12:00:00+00:00',
            },
          ],
          callsTruncated: false,
        },
      },
      {
        method: 'POST',
        path: '/api/users/{id}/reset-password',
        description: 'Set a new temporary password for a user. Sets mustResetPassword flag.',
        roles: ['ROLE_ADMIN'],
        requestBody: { password: 'TempPassword1!' },
        responseBody: { message: 'Password reset successfully' },
      },
      {
        method: 'DELETE',
        path: '/api/users/{id}/integrations/{provider}',
        description: 'Remove a social login integration (google | apple) from a user account.',
        roles: ['ROLE_ADMIN'],
        responseBody: null,
      },
      {
        method: 'DELETE',
        path: '/api/users/{id}',
        description: 'Deactivate a user account. The user can no longer sign in.',
        roles: ['ROLE_ADMIN'],
        responseBody: null,
      },
    ],
  },

  // ─── Meal Plans ──────────────────────────────────────
  {
    name: 'Meal Plans',
    description:
      'Plan meals on specific dates. Supports single-day meals and multi-day meal prep ranges.',
    endpoints: [
      {
        method: 'GET',
        path: '/api/meal-plans',
        description: 'Get meal plans within a date range.',
        roles: ['ROLE_USER'],
        queryParams: [
          { name: 'from', type: 'date', required: true, description: 'Start date (YYYY-MM-DD)' },
          { name: 'to', type: 'date', required: true, description: 'End date (YYYY-MM-DD)' },
        ],
        responseBody: [
          {
            id: 'uuid',
            startDate: '2024-03-01',
            endDate: '2024-03-01',
            mealType: 'lunch',
            eatingOut: false,
            recipe: { id: 'uuid', title: 'Pasta Bolognese' },
          },
        ],
      },
      {
        method: 'POST',
        path: '/api/meal-plans',
        description:
          'Create a meal plan entry. Set startDate === endDate for a single day; different dates indicate meal prep.',
        roles: ['ROLE_USER'],
        requestBody: {
          recipeId: 'uuid',
          startDate: '2024-03-01',
          endDate: '2024-03-01',
          mealType: 'lunch',
          eatingOut: false,
        },
        responseBody: {
          id: 'uuid',
          startDate: '2024-03-01',
          endDate: '2024-03-01',
          mealType: 'lunch',
        },
      },
      {
        method: 'PUT',
        path: '/api/meal-plans/{id}',
        description: 'Update a meal plan entry (dates, meal type, eating out flag).',
        roles: ['ROLE_USER'],
        requestBody: {
          startDate: '2024-03-02',
          endDate: '2024-03-02',
          mealType: 'dinner',
          eatingOut: true,
        },
        responseBody: { id: 'uuid', mealType: 'dinner', eatingOut: true },
      },
      {
        method: 'DELETE',
        path: '/api/meal-plans/{id}',
        description: 'Delete a meal plan entry. Returns 204 No Content.',
        roles: ['ROLE_USER'],
        responseBody: null,
      },
    ],
  },

  // ─── Shopping Carts ──────────────────────────────────
  {
    name: 'Shopping Carts',
    description:
      'Create and manage shopping carts. Carts can be sourced from recipes, meal plans, or a calendar range. Shared carts are publicly accessible via a token.',
    endpoints: [
      {
        method: 'GET',
        path: '/api/shopping-carts',
        description: "List the current user's shopping carts.",
        roles: ['ROLE_USER'],
        responseBody: {
          items: [
            {
              id: 'uuid',
              name: 'Weekly Shop',
              status: 'active',
              source: 'calendar',
              shareToken: 'uuid',
            },
          ],
          total: 3,
        },
      },
      {
        method: 'POST',
        path: '/api/shopping-carts',
        description: 'Create a new shopping cart manually.',
        roles: ['ROLE_USER'],
        requestBody: { name: 'Weekly Shop' },
        responseBody: { id: 'uuid', name: 'Weekly Shop', status: 'draft', source: 'manual' },
      },
      {
        method: 'GET',
        path: '/api/shopping-carts/{id}',
        description: 'Get a shopping cart with its sections and items.',
        roles: ['ROLE_USER'],
        responseBody: {
          id: 'uuid',
          name: 'Weekly Shop',
          status: 'active',
          sections: [
            {
              id: 'uuid',
              name: 'Produce',
              items: [{ id: 'uuid', name: 'Tomatoes', quantity: 2, checked: false }],
            },
          ],
        },
      },
      {
        method: 'DELETE',
        path: '/api/shopping-carts/{id}',
        description: 'Delete a shopping cart. Returns 204 No Content.',
        roles: ['ROLE_USER'],
        responseBody: null,
      },
      {
        method: 'POST',
        path: '/api/shopping-carts/{id}/fulfill',
        description:
          'Mark a cart as fulfilled. Clears the shareToken so shared links stop working.',
        roles: ['ROLE_USER'],
        responseBody: { id: 'uuid', status: 'fulfilled' },
      },
      {
        method: 'POST',
        path: '/api/shopping-carts/{id}/sections',
        description: 'Add a section to a cart (e.g., Produce, Dairy).',
        roles: ['ROLE_USER'],
        requestBody: { name: 'Produce' },
        responseBody: { id: 'uuid', name: 'Produce', items: [] },
      },
      {
        method: 'POST',
        path: '/api/shopping-carts/{id}/sections/{sectionId}/items',
        description: 'Add an item to a cart section.',
        roles: ['ROLE_USER'],
        requestBody: { name: 'Tomatoes', quantity: 2, unit: 'kg', price: 1.5 },
        responseBody: { id: 'uuid', name: 'Tomatoes', quantity: 2, checked: false },
      },
      {
        method: 'PATCH',
        path: '/api/shopping-carts/items/{itemId}',
        description: 'Update an item (name, quantity, checked state, price).',
        roles: ['ROLE_USER'],
        requestBody: { checked: true, price: 2.0 },
        responseBody: { id: 'uuid', checked: true, price: 2.0 },
      },
      {
        method: 'DELETE',
        path: '/api/shopping-carts/items/{itemId}',
        description: 'Remove an item from a cart. Returns 204 No Content.',
        roles: ['ROLE_USER'],
        responseBody: null,
      },
      {
        method: 'GET',
        path: '/api/shopping-carts/shared/{token}',
        description:
          'Get a shared cart by its shareToken. No authentication required — anyone with the link can view.',
        roles: ['PUBLIC'],
        responseBody: {
          id: 'uuid',
          name: 'Weekly Shop',
          sections: [
            { name: 'Produce', items: [{ id: 'uuid', name: 'Tomatoes', checked: false }] },
          ],
        },
      },
      {
        method: 'PATCH',
        path: '/api/shopping-carts/shared/{token}/items/{itemId}',
        description: "Toggle an item's checked state on a shared cart. No authentication required.",
        roles: ['PUBLIC'],
        requestBody: { checked: true },
        responseBody: { id: 'uuid', checked: true },
      },
      {
        method: 'POST',
        path: '/api/shopping-carts/from-recipe/{id}',
        description: "Generate a shopping cart from a single recipe's ingredients.",
        roles: ['ROLE_USER'],
        responseBody: { id: 'uuid', name: 'Pasta Bolognese', source: 'recipe', status: 'draft' },
      },
      {
        method: 'POST',
        path: '/api/shopping-carts/from-meal-plan',
        description: 'Generate a shopping cart from selected meal plan IDs.',
        roles: ['ROLE_USER'],
        requestBody: { mealPlanIds: ['uuid', 'uuid'] },
        responseBody: { id: 'uuid', source: 'meal_plan', status: 'draft' },
      },
      {
        method: 'POST',
        path: '/api/shopping-carts/from-calendar',
        description:
          'Generate a shopping cart from all meal plans in a date range. Skips eating-out entries. Returns 422 NO_SHOPPABLE_PLANS if all plans are eating out.',
        roles: ['ROLE_USER'],
        requestBody: { from: '2024-03-01', to: '2024-03-07' },
        responseBody: { id: 'uuid', source: 'calendar', status: 'draft' },
      },
    ],
  },
  // ─── Device Tokens ───────────────────────────────────
  {
    name: 'Device Tokens',
    description: 'Register and deregister FCM push notification tokens for Android and iOS.',
    endpoints: [
      {
        method: 'POST',
        path: '/api/devices/token',
        description:
          'Register or update an FCM device token. Upserts: reassigns token if it moved to a new user, replaces old token if the same user re-registers on the same platform. Returns 204.',
        roles: ['ROLE_USER'],
        requestBody: { token: 'fcm-registration-token', platform: 'android' },
        responseBody: null,
      },
      {
        method: 'DELETE',
        path: '/api/devices/token',
        description: 'Deregister an FCM token on logout. Returns 204.',
        roles: ['ROLE_USER'],
        requestBody: { token: 'fcm-registration-token' },
        responseBody: null,
      },
    ],
  },

  // ─── Subscriptions ───────────────────────────────────
  {
    name: 'Subscriptions',
    description:
      'Manage subscription upgrades via Stripe (web) or Google Play (Android). Tiers: free | plus | pro.',
    endpoints: [
      {
        method: 'POST',
        path: '/api/subscriptions/checkout',
        description:
          'Create a Stripe Checkout session. Price ID is selected by user country (MXN for Mexico, USD otherwise). Redirect the user to the returned URL.',
        roles: ['ROLE_USER'],
        requestBody: {
          tier: 'plus | pro',
          period: 'monthly | annual',
          successUrl: 'https://example.com/success',
          cancelUrl: 'https://example.com/cancel',
        },
        responseBody: { url: 'https://checkout.stripe.com/...' },
      },
      {
        method: 'POST',
        path: '/api/subscriptions/webhook',
        description:
          'Stripe webhook receiver. Handles checkout.session.completed, customer.subscription.updated, customer.subscription.deleted. Idempotent — duplicate events are ignored via ProcessedWebhookEvent.',
        roles: ['PUBLIC'],
        requestBody: { '(raw Stripe event payload)': '' },
        responseBody: null,
      },
      {
        method: 'GET',
        path: '/api/subscriptions/portal',
        description:
          'Get a Stripe Customer Portal URL so a paid user can manage or cancel their subscription.',
        roles: ['ROLE_USER'],
        queryParams: [
          {
            name: 'returnUrl',
            type: 'string',
            required: true,
            description: 'URL to return to after portal session',
          },
        ],
        responseBody: { url: 'https://billing.stripe.com/session/...' },
      },
      {
        method: 'POST',
        path: '/api/subscriptions/google-play/verify',
        description:
          'Verify a Google Play subscription purchase token via the Android Publisher API v3. Product ID naming convention: tier prefix (plus_ / pro_) + period suffix (_monthly / _annual), e.g. plus_monthly.',
        roles: ['ROLE_USER'],
        requestBody: { purchaseToken: 'google-play-purchase-token', productId: 'plus_monthly' },
        responseBody: {
          id: 'uuid',
          email: 'jane@example.com',
          name: 'Jane Smith',
          subscriptionTier: 'plus',
          billingPeriod: 'monthly',
          subscriptionPeriodEnd: '2026-06-22T00:00:00+00:00',
        },
      },
    ],
  },

  // ─── Support Tickets ─────────────────────────────────
  {
    name: 'Support Tickets',
    description:
      'User-submitted support tickets. Any authenticated user can create a ticket. ROLE_SUPPORT (and ROLE_ADMIN) can list, view, reply, update, and escalate tickets to Linear.',
    endpoints: [
      {
        method: 'POST',
        path: '/api/support-tickets',
        description:
          "Submit a new support ticket. The authenticated user becomes the reporter. Returns 403 SUPPORT_RESTRICTED if the user's support access has been revoked.",
        roles: ['ROLE_USER'],
        requestBody: {
          type: 'bug | suggestion | error | other',
          subject: 'Cannot save recipe',
          body: 'When I click Save, nothing happens and no error is shown.',
          platform: 'web | ios | android',
          appVersion: '1.2.3',
        },
        responseBody: {
          id: 'uuid',
          type: 'bug',
          status: 'open',
          priority: 'medium',
          subject: 'Cannot save recipe',
          platform: 'web',
          createdAt: '2024-01-01T00:00:00Z',
        },
      },
      {
        method: 'GET',
        path: '/api/support-tickets',
        description: 'Paginated list of all tickets. Supports filtering by status and type.',
        roles: ['ROLE_SUPPORT'],
        queryParams: [
          { name: 'page', type: 'integer', description: 'Page number (default 1)' },
          { name: 'perPage', type: 'integer', description: 'Items per page, max 50 (default 20)' },
          {
            name: 'status',
            type: 'string',
            description: 'Filter by status: open | in_progress | resolved | closed',
          },
          {
            name: 'type',
            type: 'string',
            description: 'Filter by type: bug | suggestion | error | other',
          },
        ],
        responseBody: {
          items: [
            {
              id: 'uuid',
              type: 'bug',
              status: 'open',
              priority: 'medium',
              subject: 'Cannot save recipe',
              readBySupport: false,
              messageCount: 2,
            },
          ],
          total: 42,
          page: 1,
          perPage: 20,
        },
      },
      {
        method: 'GET',
        path: '/api/support-tickets/unread-count',
        description:
          'Returns the number of tickets not yet read by the support team. Used for the sidebar badge.',
        roles: ['ROLE_SUPPORT'],
        responseBody: { count: 7 },
      },
      {
        method: 'GET',
        path: '/api/support-tickets/my',
        description:
          "Returns the authenticated user's own tickets ordered by creation date. Returns 403 SUPPORT_RESTRICTED if the user's support access has been revoked.",
        roles: ['ROLE_USER'],
        responseBody: [
          {
            id: 'uuid',
            type: 'bug',
            status: 'open',
            subject: 'Cannot save recipe',
            messageCount: 1,
            createdAt: '2024-01-01T00:00:00Z',
          },
        ],
      },
      {
        method: 'GET',
        path: '/api/support-tickets/{id}',
        description:
          'Get full ticket detail including body and threaded messages. Marks the ticket as read by support.',
        roles: ['ROLE_SUPPORT'],
        responseBody: {
          id: 'uuid',
          type: 'bug',
          status: 'open',
          priority: 'medium',
          subject: 'Cannot save recipe',
          body: 'When I click Save…',
          isEscalated: false,
          linearIssueUrl: null,
          user: { id: 'uuid', name: 'Jane Smith', email: 'jane@example.com' },
          messages: [
            {
              id: 'uuid',
              body: 'Thanks, we are looking into it.',
              fromSupport: true,
              isInternal: false,
              createdAt: '2024-01-02T00:00:00Z',
            },
          ],
        },
      },
      {
        method: 'PATCH',
        path: '/api/support-tickets/{id}',
        description: 'Update ticket status, priority, or assigned support agent.',
        roles: ['ROLE_SUPPORT'],
        requestBody: { status: 'in_progress', priority: 'high', assignedToId: 'uuid | null' },
        responseBody: { id: 'uuid', status: 'in_progress', priority: 'high' },
      },
      {
        method: 'POST',
        path: '/api/support-tickets/{id}/messages',
        description:
          'Add a reply to the thread. Support agents can mark a message as internal (not visible to the user). Replying re-opens a resolved ticket if the sender is the reporter. Returns 403 SUPPORT_RESTRICTED if a non-support user has had their access revoked.',
        roles: ['ROLE_USER'],
        requestBody: { body: 'Any update on this?', isInternal: false },
        responseBody: {
          id: 'uuid',
          body: 'Any update on this?',
          fromSupport: false,
          isInternal: false,
          createdAt: '2024-01-03T00:00:00Z',
        },
      },
      {
        method: 'POST',
        path: '/api/support-tickets/{id}/escalate',
        description:
          'Create a Linear issue from this ticket for the dev team. Stores the issue ID and URL. Returns 422 ALREADY_ESCALATED if already escalated, 502 LINEAR_ERROR on API failure.',
        roles: ['ROLE_SUPPORT'],
        responseBody: {
          linearIssueId: 'abc123',
          linearIssueUrl: 'https://linear.app/team/issue/abc123',
        },
      },
    ],
  },
]
